/**
 * Portfolio verification — an investor's "I invested in X", confirmed by X.
 *
 * The server link between two screens that were both built and never connected:
 * onboarding `portfolio-companies.tsx` (investor files claims) and
 * `(startup)/verification-requests.tsx` (founder answers them). Until now the
 * first wrote to a Zustand slice and the second read a mock array, so a claim
 * could never reach the founder it named — the feature only ever worked in mock
 * mode. See supabase/migrations/20260731120000_portfolio_verification_requests.sql.
 *
 * IDENTITY: `investor_user_id` is the AUTH user id, matching
 * `portfolio_companies` and `investor_track_record`.
 */

import { escapeLikePattern } from '@/lib/sql/escape-like';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isSupabaseConfigured, supabase } from './client';
import type { Database } from './database.types';

type VerificationRow =
  Database['public']['Tables']['portfolio_verification_requests']['Row'];

export type VerificationStatus = 'pending' | 'verified' | 'rejected';

export interface PortfolioVerification {
  id: string;
  investorUserId: string;
  /** Claimant name as claimed — snapshotted, never joined live. */
  investorName: string;
  /** For syndicate deals, the lead the founder actually dealt with. */
  syndicateLeadName?: string;
  investmentType?: 'direct' | 'syndicate';
  /** When the investor says the investment happened. */
  investmentDate?: string;
  companyName: string;
  website?: string;
  founderEmail?: string;
  /** null when the claimed company is not on the platform yet. */
  startupId: string | null;
  status: VerificationStatus;
  requestedAt: string;
  respondedAt?: string;
}

export const verificationKeys = {
  mine: (userId: string) => ['portfolioVerification', 'mine', userId] as const,
  incoming: (startupId: string) => ['portfolioVerification', 'incoming', startupId] as const,
};

function rowToVerification(row: VerificationRow): PortfolioVerification {
  return {
    id: row.id,
    investorUserId: row.investor_user_id,
    investorName: row.investor_name,
    syndicateLeadName: row.syndicate_lead_name ?? undefined,
    investmentType: (row.investment_type ?? undefined) as 'direct' | 'syndicate' | undefined,
    investmentDate: row.investment_date ?? undefined,
    companyName: row.company_name,
    website: row.website ?? undefined,
    founderEmail: row.founder_email ?? undefined,
    startupId: row.startup_id,
    status: row.status as VerificationStatus,
    requestedAt: row.created_at,
    respondedAt: row.responded_at ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * Which `startup_profiles` row a claimed company name refers to, if any.
 *
 * Resolved through `company_directory` rather than by querying
 * `startup_profiles` directly: an investor's RLS only exposes actively-raising
 * profiles, so a direct lookup would fail to resolve every company that is not
 * currently raising — i.e. most of them. The directory is readable and already
 * carries `startup_profile_id`.
 *
 * Returns null when the company is unknown. That is a normal outcome, not an
 * error: the claim is still filed, stays `startup_id IS NULL`, and remains
 * visible only to its author until the company joins.
 */
export async function resolveClaimedStartupId(
  companyName: string,
): Promise<string | null> {
  const name = companyName.trim();
  if (!name || !isSupabaseConfigured()) return null;

  const { data, error } = await supabase
    .from('company_directory')
    .select('startup_profile_id, name')
    .ilike('name', escapeLikePattern(name))
    .not('startup_profile_id', 'is', null)
    .limit(5);

  // A failed lookup must not sink the claim — file it unresolved.
  if (error || !data || data.length === 0) return null;

  const exact = data.find(
    (row) => (row.name ?? '').trim().toLowerCase() === name.toLowerCase(),
  );
  return exact?.startup_profile_id ?? null;
}

// ---------------------------------------------------------------------------
// Investor side
// ---------------------------------------------------------------------------

/** The signed-in investor's own claims, resolved or not. */
export function useMyPortfolioVerifications(userId: string | undefined) {
  return useQuery({
    queryKey: verificationKeys.mine(userId ?? ''),
    queryFn: async (): Promise<PortfolioVerification[]> => {
      if (!userId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('portfolio_verification_requests')
        .select('*')
        .eq('investor_user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []).map(rowToVerification);
    },
    enabled: !!userId && isSupabaseConfigured(),
  });
}

export interface PortfolioClaimInput {
  companyName: string;
  website?: string;
  founderEmail?: string;
  /**
   * `investmentType: 'syndicate'` REQUIRES `syndicateLeadName`. A syndicate
   * participant is usually a name the founder has never seen — the cheque, the
   * negotiation and the cap-table line sit with the lead — so without it the
   * founder is asked about someone they cannot recognise and a correct claim
   * gets rejected. Enforced in the database (pvr_syndicate_lead_required), not
   * just here.
   */
  investmentType?: 'direct' | 'syndicate';
  syndicateLeadName?: string;
  /** ISO date. The founder needs a "when" to recognise the deal. */
  investmentDate?: string;
}

/**
 * File claims for a batch of companies, returning how many were written.
 *
 * Upsert on `(investor_user_id, company_name_key)` so re-running onboarding or
 * double-tapping Continue does not file the same claim twice and show the
 * founder duplicates. `ignoreDuplicates` keeps an ALREADY-ANSWERED claim
 * untouched — re-filing must never reset a founder's verdict back to pending.
 *
 * THROWS on failure. Callers must surface it.
 */
export async function filePortfolioClaims(
  userId: string,
  /** The claimant's full name. Required — an unattributable claim is unanswerable. */
  investorName: string,
  claims: PortfolioClaimInput[],
): Promise<number> {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured');

  if (!investorName.trim()) {
    throw new Error('Cannot file a portfolio claim without the investor name');
  }

  const named = claims.filter((c) => c.companyName.trim().length > 0);
  if (named.length === 0) return 0;

  // Dedupe within the batch: Postgres rejects an ON CONFLICT batch containing
  // the same key twice, which would reject every claim over one repeat.
  const deduped = [
    ...named
      .reduce((acc, claim) => {
        acc.set(claim.companyName.trim().toLowerCase(), claim);
        return acc;
      }, new Map<string, PortfolioClaimInput>())
      .values(),
  ];

  const rows = await Promise.all(
    deduped.map(async (claim) => ({
      investor_user_id: userId,
      investor_name: investorName.trim(),
      company_name: claim.companyName.trim(),
      website: claim.website?.trim() || null,
      founder_email: claim.founderEmail?.trim() || null,
      investment_type: claim.investmentType ?? null,
      syndicate_lead_name: claim.syndicateLeadName?.trim() || null,
      investment_date: claim.investmentDate ?? null,
      startup_id: await resolveClaimedStartupId(claim.companyName),
    })),
  );

  const { error } = await supabase
    .from('portfolio_verification_requests')
    .upsert(rows, {
      onConflict: 'investor_user_id,company_name_key',
      ignoreDuplicates: true,
    });

  if (error) throw error;
  return rows.length;
}

// ---------------------------------------------------------------------------
// Founder side
// ---------------------------------------------------------------------------

/**
 * Claims awaiting this startup's answer.
 *
 * RLS restricts rows to claims naming a startup the caller is an active member
 * of, so passing a startup id the caller does not belong to simply returns
 * nothing rather than leaking.
 */
export function useIncomingVerifications(startupId: string | undefined) {
  return useQuery({
    queryKey: verificationKeys.incoming(startupId ?? ''),
    queryFn: async (): Promise<PortfolioVerification[]> => {
      if (!startupId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('portfolio_verification_requests')
        .select('*')
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []).map(rowToVerification);
    },
    enabled: !!startupId && isSupabaseConfigured(),
  });
}

/**
 * Answer a claim.
 *
 * `authUserId` is recorded as `responded_by`. The founder's UPDATE grant covers
 * only `status`, `responded_by` and `responded_at`, so this cannot become an
 * edit of the claim itself — attempting one fails at the grant, not silently.
 */
export function useRespondToVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      startupId: string;
      authUserId: string;
      status: Exclude<VerificationStatus, 'pending'>;
    }) => {
      if (!isSupabaseConfigured()) throw new Error('Supabase not configured');

      const { data, error } = await supabase
        .from('portfolio_verification_requests')
        .update({
          status: vars.status,
          responded_by: vars.authUserId,
          responded_at: new Date().toISOString(),
        })
        .eq('id', vars.id)
        .select('id');

      if (error) throw error;
      // RLS filters rather than errors: a caller without membership updates
      // ZERO rows and gets no error. Reporting success then would be the same
      // silent-no-op that made data-room approval claim to grant access it
      // never granted.
      if (!data || data.length === 0) {
        throw new Error('That request could not be updated. It may already have been answered.');
      }
    },
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({ queryKey: verificationKeys.incoming(vars.startupId) });
    },
  });
}
